import { useEffect, useRef, type RefObject } from "react"

export interface HotkeyOptions {
  /**
   * Limits the shortcut to key presses that start inside this element. Omit it for a global shortcut. A scoped shortcut
   * beats a global one for the same keys, and the innermost scope beats an outer one.
   */
  readonly scope?: RefObject<HTMLElement | null> | HTMLElement | null
  /** Turns the shortcut off without unregistering it. Defaults to true. */
  readonly enabled?: boolean
  /**
   * Whether the shortcut also fires while focus is in a text field. Defaults to true when the shortcut holds Ctrl,
   * Meta (Cmd) or Alt, and to false for plain keys, so typing is never taken over.
   */
  readonly allowInEditable?: boolean
  /** Calls `preventDefault` on the key press when the shortcut fires. Defaults to true. */
  readonly preventDefault?: boolean
  /**
   * Makes the shortcut a hold: the handler runs once when the keys go down (not for key repeats), and `onRelease` runs
   * when one of the keys comes up again, when the window loses focus, or when the component unmounts. A shortcut can be a modifier key alone, such as `alt`.
   */
  readonly onRelease?: () => void
}

export type HotkeyHandler = (event: KeyboardEvent) => void

interface ParsedHotkey {
  readonly key: string
  readonly mod: boolean
  readonly ctrl: boolean
  readonly meta: boolean
  readonly alt: boolean
  readonly shift: boolean
}

interface HotkeyRegistration {
  readonly hotkey: ParsedHotkey
  readonly options: () => HotkeyOptions
  readonly handler: () => HotkeyHandler
  /** Whether the keys are currently held down, for a shortcut with `onRelease`. */
  held: boolean
}

const modifierKeys: Readonly<Record<string, "ctrl" | "meta" | "alt" | "shift">> = {
  control: "ctrl",
  ctrl: "ctrl",
  meta: "meta",
  cmd: "meta",
  alt: "alt",
  option: "alt",
  shift: "shift",
}

const keyAliases: Readonly<Record<string, string>> = {
  esc: "escape",
  space: " ",
  spacebar: " ",
  plus: "+",
  del: "delete",
  up: "arrowup",
  down: "arrowdown",
  left: "arrowleft",
  right: "arrowright",
}

/**
 * Parses a shortcut such as `mod+b`, `ctrl+.`, `shift+alt+k` or `escape`. `mod` is Ctrl or Cmd, whichever the user's
 * platform uses. Throws for text without a key or with an unknown modifier.
 */
export function parseHotkey(shortcut: string): ParsedHotkey {
  const parts = shortcut
    .split("+")
    .map((part) => part.trim().toLowerCase())
    .reduce<string[]>((result, part, index, all) => {
      // "ctrl++" and "ctrl+plus" both mean the plus key.
      if (part === "" && index > 0 && all[index - 1] !== "") result.push("+")
      else if (part !== "") result.push(part)
      return result
    }, [])
  const key = parts.pop()
  if (key === undefined) throw new Error(`Invalid hotkey "${shortcut}".`)
  const modifiers = new Set(parts)
  for (const modifier of modifiers) {
    if (!["mod", "ctrl", "control", "meta", "cmd", "alt", "option", "shift"].includes(modifier)) {
      throw new Error(`Invalid hotkey "${shortcut}": unknown modifier "${modifier}".`)
    }
  }
  // A modifier key on its own, such as "alt", is down when its own flag is set.
  const own = modifierKeys[key]
  return {
    key:
      own === undefined ? (keyAliases[key] ?? key) : { ctrl: "control", meta: "meta", alt: "alt", shift: "shift" }[own],
    mod: modifiers.has("mod"),
    ctrl: modifiers.has("ctrl") || modifiers.has("control") || own === "ctrl",
    meta: modifiers.has("meta") || modifiers.has("cmd") || own === "meta",
    alt: modifiers.has("alt") || modifiers.has("option") || own === "alt",
    shift: modifiers.has("shift") || own === "shift",
  }
}

type HotkeyEvent = Pick<KeyboardEvent, "key" | "altKey" | "shiftKey" | "ctrlKey" | "metaKey">

function matches(hotkey: ParsedHotkey, event: HotkeyEvent): boolean {
  if (event.key.toLowerCase() !== hotkey.key) return false
  if (event.altKey !== hotkey.alt) return false
  // Shifted symbols such as "?" already carry the shift in their key.
  if (event.shiftKey !== hotkey.shift && !(hotkey.key.length === 1 && !/[a-z0-9]/.test(hotkey.key))) return false
  if (hotkey.mod) return event.ctrlKey || event.metaKey
  return event.ctrlKey === hotkey.ctrl && event.metaKey === hotkey.meta
}

const parsedShortcuts = new Map<string, ParsedHotkey>()

/**
 * Whether a key press (a DOM or React keyboard event) is exactly `shortcut`, such as `mod+d` or `delete`. For
 * components that handle keys themselves, for example a row-scoped shortcut, instead of registering a global one
 * with `useHotkey`. Throws for an invalid shortcut.
 */
export function matchesHotkey(shortcut: string, event: HotkeyEvent): boolean {
  let hotkey = parsedShortcuts.get(shortcut)
  if (hotkey === undefined) {
    hotkey = parseHotkey(shortcut)
    parsedShortcuts.set(shortcut, hotkey)
  }
  return matches(hotkey, event)
}

function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return target.isContentEditable || target.closest("input, textarea, select") !== null
}

function resolveScope(scope: HotkeyOptions["scope"]): HTMLElement | null | undefined {
  if (scope === undefined) return undefined
  if (scope instanceof HTMLElement) return scope
  return scope === null ? null : "current" in scope ? scope.current : undefined
}

const registrations: HotkeyRegistration[] = []

function allowsEditable(registration: HotkeyRegistration): boolean {
  const { hotkey } = registration
  return registration.options().allowInEditable ?? (hotkey.mod || hotkey.ctrl || hotkey.meta || hotkey.alt)
}

/** Both scopes contain the key press, so they are nested: the deeper scope wins, and an equal one is a tie. */
function beats(scope: HTMLElement | undefined, winnerScope: HTMLElement | undefined): boolean {
  if (winnerScope === undefined) return true
  if (scope === undefined) return false
  return scope === winnerScope || winnerScope.contains(scope)
}

function handleKeyDown(event: KeyboardEvent): void {
  if (event.defaultPrevented || event.isComposing) return
  const editable = isEditable(event.target)
  const target = event.target instanceof Node ? event.target : null
  let winner: HotkeyRegistration | undefined
  let winnerScope: HTMLElement | undefined
  // Later registrations win a tie, so a component mounted later can override an earlier shortcut.
  for (const registration of registrations) {
    const options = registration.options()
    if (options.enabled === false || !matches(registration.hotkey, event)) continue
    if (editable && !allowsEditable(registration)) continue
    const scope = resolveScope(options.scope)
    if (scope === null) continue
    if (scope !== undefined && (target === null || !scope.contains(target))) continue
    if (winner !== undefined && !beats(scope, winnerScope)) continue
    winner = registration
    winnerScope = scope
  }
  if (winner === undefined) return
  const options = winner.options()
  if (options.onRelease !== undefined) {
    if (event.repeat) return
    winner.held = true
  }
  if (options.preventDefault !== false) event.preventDefault()
  winner.handler()(event)
}

/** The keys that make up a held shortcut: its key and its modifiers. */
function isHeldKey(hotkey: ParsedHotkey, key: string): boolean {
  const lower = key.toLowerCase()
  return (
    lower === hotkey.key ||
    (hotkey.alt && lower === "alt") ||
    (hotkey.shift && lower === "shift") ||
    ((hotkey.ctrl || hotkey.mod) && lower === "control") ||
    ((hotkey.meta || hotkey.mod) && lower === "meta")
  )
}

function release(registration: HotkeyRegistration): void {
  if (!registration.held) return
  registration.held = false
  registration.options().onRelease?.()
}

function handleKeyUp(event: KeyboardEvent): void {
  for (const registration of [...registrations]) {
    if (registration.held && isHeldKey(registration.hotkey, event.key)) release(registration)
  }
}

function handleBlur(): void {
  for (const registration of [...registrations]) release(registration)
}

function register(registration: HotkeyRegistration): () => void {
  if (registrations.length === 0) {
    window.addEventListener("keydown", handleKeyDown)
    window.addEventListener("keyup", handleKeyUp)
    window.addEventListener("blur", handleBlur)
  }
  registrations.push(registration)
  return () => {
    release(registration)
    registrations.splice(registrations.indexOf(registration), 1)
    if (registrations.length === 0) {
      window.removeEventListener("keydown", handleKeyDown)
      window.removeEventListener("keyup", handleKeyUp)
      window.removeEventListener("blur", handleBlur)
    }
  }
}

/**
 * Runs `handler` when the user presses `shortcut` (for example `mod+b` or `ctrl+.`), until the component unmounts.
 *
 * Conflict-safe: one key press runs at most one handler. A shortcut scoped to an element beats a global one, the
 * innermost scope beats an outer one, and the most recently mounted registration wins a tie. Key presses another
 * handler already canceled with `preventDefault` are ignored, and plain keys do not fire while the user types in a
 * text field (see `allowInEditable`). With `onRelease` the shortcut is a hold, such as a modifier key kept down.
 */
export function useHotkey(shortcut: string, handler: HotkeyHandler, options: HotkeyOptions = {}): void {
  const handlerRef = useRef(handler)
  handlerRef.current = handler
  const optionsRef = useRef(options)
  optionsRef.current = options

  useEffect(() => {
    return register({
      hotkey: parseHotkey(shortcut),
      options: () => optionsRef.current,
      handler: () => handlerRef.current,
      held: false,
    })
  }, [shortcut])
}

/** The label of a shortcut for the user's platform, such as `Ctrl+B` or `Cmd+B`. */
export function formatHotkey(
  shortcut: string,
  platform = typeof navigator === "undefined" ? "" : navigator.platform
): string {
  const hotkey = parseHotkey(shortcut)
  const apple = /mac|iphone|ipad/i.test(platform)
  const parts: string[] = []
  if (hotkey.ctrl || (hotkey.mod && !apple)) parts.push("Ctrl")
  if (hotkey.alt) parts.push(apple ? "Option" : "Alt")
  if (hotkey.shift) parts.push("Shift")
  if (hotkey.meta || (hotkey.mod && apple)) parts.push(apple ? "Cmd" : "Win")
  const key = hotkey.key === " " ? "Space" : hotkey.key.length === 1 ? hotkey.key.toUpperCase() : hotkey.key
  parts.push(key.length > 1 ? key[0]!.toUpperCase() + key.slice(1) : key)
  return [...new Set(parts)].join("+")
}
